<template>
  <div v-if="isMessagesLoading">
    <div class="animate-pulse">
      <div class="h-4 bg-slate-200 rounded w-3/4 mb-4"></div>
      <div class="h-32 bg-slate-200 rounded mb-4"></div>
      <div class="space-y-3">
        <div class="h-4 bg-slate-200 rounded w-1/2"></div>
        <div class="h-4 bg-slate-200 rounded w-2/3"></div>
      </div>
    </div>
  </div>
  <div v-else>
    <AppTabs 
      :items="[
        { label: 'Comments', slot: 'comments' },
        { label: 'Progress Logs', slot: 'progress-logs' }
      ]"
    >
      <template #comments>
        <div v-if="commentMessages && commentMessages.length > 0">
          <ProjectMessageThread
            :messages="commentMessages"
            message-type="comment"
          />
          <MessageForm
            :project="project"
            :message-type="MESSAGE_TYPES.COMMENT"
            :save-funtion="(value) => createMessage({ message: value, id: 'comments' })"
          />
        </div>
        <p v-else class="text-sm text-gray-500 italic">No comments yet</p>
      </template>
      
      <template #progress-logs>
        <div v-if="progressLogMessages && progressLogMessages.length > 0">
          <ProjectMessageThread
          :messages="progressLogMessages"
            message-type="progress_log"
          />
          <MessageForm
            :project="project"
            :message-type="MESSAGE_TYPES.PROGRESS_LOG"
            :save-funtion="(value) => createMessage({ message: value, id: 'progress-logs' })"
          />
        </div>
        <p v-else class="text-sm text-gray-500 italic">No progress logs yet</p>
      </template>
    </AppTabs>
  </div>
</template>

<script setup lang="ts">
import { computed, PropType } from 'vue'
import { Project } from '@/models/project'
import { MESSAGE_TYPES } from '@/models/message'
import ProjectMessageThread from '@/modules/Project/components/ProjectMessageThread.vue'
import AppTabs from '@/components/app/AppTabs.vue'
import MessageService from '@/services/MessageService'
import { eq, orderByDesc, inArray } from '@/utils/queryFilters'
import { useMutation, useQuery } from '@tanstack/vue-query'
import MessageForm from '@/modules/Project/components/ProjectMessage.vue'
import { Message } from '@/models/message'
import ProjectService from '@/services/ProjectService'

const props = defineProps({
  project: {
    type: Object as PropType<Project>,
    required: true
  }
})

// Get recent messages, excluding internal notes
const { data: messages, isLoading: isMessagesLoading } = useQuery({
  queryKey: ['messages', props.project.id],
  queryFn: async () => {
    const messages = await new MessageService('').where(
      [
        eq('projectId', props.project.id),
        inArray('type', [MESSAGE_TYPES.COMMENT, MESSAGE_TYPES.PROGRESS_LOG])
      ],
      [orderByDesc('createdAt')]
    )

    return messages.sort((a, b) => {
      // Ensure createdAt is a Date object before calling getTime()
      const dateA = a.createdAt instanceof Date ? a.createdAt : new Date(a.createdAt)
      const dateB = b.createdAt instanceof Date ? b.createdAt : new Date(b.createdAt)
      return dateB.getTime() - dateA.getTime()
    })
  }
})
const commentMessages = computed(() => {
  return messages.value?.filter(msg => msg.type === MESSAGE_TYPES.COMMENT) || []
})

const progressLogMessages = computed(() => {
  return messages.value?.filter(msg => msg.type === MESSAGE_TYPES.PROGRESS_LOG) || []
})

const { mutateAsync: createMessage} = useMutation({
  mutationFn: async (payload: {message: Message; id: string}) => {
    const newMessage = await new ProjectService().addMessageToProject(props.project.id, props.project.customerId, payload.message)
    return { newMessage, id: payload.id }
  }
})
</script>